import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Component({
  selector: 'app-rich-text-cell-renderer',
  templateUrl: './rich-text.component.html',
})
export class RichTextCellRendererComponent implements ICellRendererAngularComp {
  safeHtml: SafeHtml;
  
  constructor(private sanitizer: DomSanitizer) {}

  agInit(params: ICellRendererParams): void {
    const value = params.value || '';
    const htmlWithLinks = this.convertUrlsToLinks(value);
    const htmlWithProperLinks = this.ensureProperLinkAttributes(htmlWithLinks);
    this.safeHtml = this.sanitizer.bypassSecurityTrustHtml(htmlWithProperLinks);
  }

  refresh(params: ICellRendererParams): boolean {
    this.agInit(params);
    return true;
  }

  /**
   * Converts URLs in text to clickable links that open in a new tab
   */
  convertUrlsToLinks(text: string): string {
    if (!text) return '';
    
    // Split the HTML into tokens (tags and text)
    const tokenRegex = /(<\/?[^>]+>)|([^<]+)/g;
    const tokens = text.match(tokenRegex) || [];
    
    // Process each token
    const processedTokens = tokens.map(token => {
      // If this is an HTML tag, leave it as is
      if (token.startsWith('<') && token.endsWith('>')) {
        // Skip processing if it's an anchor tag or contains href/src attributes
        if (token.match(/<a\s|<\/a>|href=|src=/i)) {
          return token;
        }
        return token;
      }
      
      // Otherwise, convert URLs to links
      // Simple but effective regex that captures most URLs including those with query params and fragments
      const urlRegex = /(https?:\/\/[^\s<>"'\)]+)/g;
      return token.replace(urlRegex, (url) => {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: var(--primary-color);">${url}</a>`;
      });
    });
    
    // Join the tokens back together
    return processedTokens.join('');
  }

  /**
   * Ensures that existing anchor tags have proper attributes for external links
   * @param html The HTML string to process
   * @returns HTML with properly configured anchor tags
   */
  ensureProperLinkAttributes(html: string): string {
    if (!html) return '';
    
    // Find anchor tags that don't already have target="_blank" and add it
    return html.replace(/<a\s+([^>]*?)href\s*=\s*["']([^"']+)["']([^>]*?)>/gi, (match, beforeHref, url, afterHref) => {
      // Check if it's an external URL (starts with http:// or https://)
      if (url.match(/^https?:\/\//)) {
        // Check if target attribute is already present
        if (!match.match(/target\s*=/i)) {
          // Add target="_blank" and rel attributes for external links
          return `<a ${beforeHref}href="${url}"${afterHref} target="_blank" rel="noopener noreferrer" style="color: var(--primary-color);">`;
        }
      }
      return match;
    });
  }

  /**
   * Handles mousedown events on rich text content to properly open links
   * @param event The mousedown event
   */
  handleRichTextClick(event: Event): boolean | void {
    const mouseEvent = event as MouseEvent;
    if (mouseEvent.button !== 0) return; // Only handle left clicks
    
    const target = event.target as HTMLElement;
    let anchorElement: HTMLAnchorElement | null = null;
    
    if (target.tagName === 'A') {
      anchorElement = target as HTMLAnchorElement;
    } else {
      anchorElement = target.closest('a');
    }
    
    if (anchorElement && anchorElement.href) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      const originalHref = anchorElement.href;
      anchorElement.removeAttribute('href');
      
      window.open(originalHref, '_blank', 'noopener,noreferrer');
      
      setTimeout(() => {
        if (anchorElement) {
          anchorElement.setAttribute('href', originalHref);
        }
      }, 100);
      
      return false;
    }
  }
} 